<template>
  <div>
    <my-loader v-if="isLoading" />

    <div v-else class="content">
      <div class="table-page">
        <div class="list-content">
          <h2>Топ и клиенты</h2>

          <div class="create-form">
            <input
              type="text"
              v-model="form.name"
              placeholder="Название"
              :disabled="mutating"
              @keyup.enter="submitClient"
            >
            <input
              type="text"
              v-model="form.type"
              placeholder="Тип клиента"
              :disabled="mutating"
              @keyup.enter="submitClient"
            >
            <select v-model="form.category" :disabled="mutating">
              <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <input
              type="number"
              v-model="form.penalty_percent"
              placeholder="Штраф %"
              min="0"
              max="100"
              class="penalty-input"
              :disabled="mutating"
              @keyup.enter="submitClient"
            >
            <input
              type="text"
              v-model="form.comment"
              placeholder="Комментарий"
              class="comment-input"
              maxlength="2000"
              :disabled="mutating"
              @keyup.enter="submitClient"
            >
            <button
              class="hover"
              type="button"
              :disabled="mutating"
              @click="submitClient"
            >
              {{ mutating ? 'Сохранение…' : (isEditing ? 'Сохранить' : 'Добавить') }}
            </button>
            <button
              v-if="isEditing"
              class="cancel-button"
              type="button"
              :disabled="mutating"
              @click="resetForm"
            >
              Отмена
            </button>
          </div>

          <div v-if="hasError" class="state-block error-block">
            Не удалось загрузить список.
            <button class="retry-btn" type="button" @click="loadList">Повторить</button>
          </div>

          <div v-else-if="!clients.length" class="state-block">
            Список пуст. Добавьте клиента для приоритезации рейсов в TMS.
          </div>

          <table v-else class="company-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип клиента</th>
                <th>Категория</th>
                <th>Штраф %</th>
                <th>Комментарий</th>
                <th>Изменил</th>
                <th>Изменено</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="client in clients"
                :key="client.id"
                class="row-company"
                :class="{ 'row-editing': isEditing && editingId === client.id }"
              >
                <td>{{ client.name || '—' }}</td>
                <td>{{ client.type || '—' }}</td>
                <td>
                  <span :class="['category-badge', `category-${clientCategoryOf(client)}`]">
                    {{ clientCategoryLabel(clientCategoryOf(client)) }}
                  </span>
                </td>
                <td>{{ client.penalty_percent ?? 100 }}</td>
                <td class="comment-cell" :title="client.comment || ''">{{ client.comment || '—' }}</td>
                <td>{{ client.updated_by_name || '—' }}</td>
                <td>{{ formatClientUpdated(client.update_dt) }}</td>
                <td class="actions-cell">
                  <button
                    class="edit-button hover"
                    type="button"
                    :disabled="mutating"
                    @click="startEditClient(client)"
                  >
                    Изменить
                  </button>
                  <button
                    class="delete-button bg-denied hover"
                    type="button"
                    :disabled="mutating"
                    @click="deleteClient(client)"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { computed, ref, onMounted } from 'vue'
import { createToaster } from '@meforma/vue-toaster'
import {
  CLIENT_CATEGORY_OPTIONS,
  emptyTopClientForm,
  normalizeTopClientsItems,
  shouldShowTopClientsLoader,
  validateTopClientForm,
  shouldClearFormAfterCreate,
  canDeleteClient,
  canEditClient,
  buildDeleteConfirmMessage,
  clientCategoryLabel,
  clientCategoryOf,
  formatClientUpdated,
  topClientFormFromClient,
} from '@/pages/securityCheck/topClientsState.mjs'

export default {
  name: 'topClients',
  setup() {
    const store = useStore()
    const toaster = createToaster()
    const form = ref(emptyTopClientForm())
    const hasError = ref(false)
    const mutating = ref(false)
    const editingId = ref(null)
    const isEditing = computed(() => editingId.value !== null)

    const loadList = async () => {
      hasError.value = false
      const ok = await store.dispatch('apiStorage/getItems', {
        model: 'topClients',
        params: {},
      })
      hasError.value = !ok
      return ok
    }

    onMounted(() => {
      loadList()
    })

    const resetForm = () => {
      editingId.value = null
      form.value = emptyTopClientForm()
    }

    const addClient = async (payload) => {
      if (mutating.value) return

      mutating.value = true
      try {
        const created = await store.dispatch('apiStorage/addItem', {
          model: 'topClients',
          form: payload,
        })
        if (!shouldClearFormAfterCreate(created)) return
        form.value = emptyTopClientForm()
        await loadList()
      } finally {
        mutating.value = false
      }
    }

    const updateClient = async (payload) => {
      if (mutating.value) return

      mutating.value = true
      try {
        const ok = await store.dispatch('apiStorage/updateItem', {
          model: 'topClients',
          form: { id: editingId.value, ...payload },
        })
        if (ok) {
          resetForm()
          await loadList()
        }
      } finally {
        mutating.value = false
      }
    }

    const submitClient = () => {
      if (mutating.value) return

      const { ok, error, form: payload } = validateTopClientForm(form.value)
      if (!ok) {
        toaster.error(error)
        return
      }
      if (isEditing.value) {
        updateClient(payload)
      } else {
        addClient(payload)
      }
    }

    const startEditClient = (client) => {
      if (mutating.value || !canEditClient(client)) return
      editingId.value = client.id
      form.value = topClientFormFromClient(client)
    }

    const deleteClient = async (client) => {
      if (mutating.value || !canDeleteClient(client)) return
      if (!window.confirm(buildDeleteConfirmMessage(client))) return

      mutating.value = true
      try {
        const ok = await store.dispatch('apiStorage/deleteItem', {
          model: 'topClients',
          params: { client_id: client.id },
        })
        if (ok) {
          if (editingId.value === client.id) resetForm()
          await loadList()
        }
      } finally {
        mutating.value = false
      }
    }

    return {
      form,
      hasError,
      mutating,
      editingId,
      isEditing,
      categoryOptions: CLIENT_CATEGORY_OPTIONS,
      loadList,
      resetForm,
      submitClient,
      startEditClient,
      deleteClient,
      clientCategoryLabel,
      clientCategoryOf,
      formatClientUpdated,
      clients: computed(() =>
        normalizeTopClientsItems(store.state.apiStorage.topClients.items),
      ),
      isLoading: computed(() =>
        shouldShowTopClientsLoader({
          statusLoading: store.state.apiStorage.topClients.statusLoading,
          mutating: mutating.value,
        }),
      ),
    }
  },
}
</script>

<style scoped>
.create-form {
  margin-bottom: 15px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.create-form input,
.create-form select,
.create-form button {
  border-radius: 5px;
  border: 1px solid #ccc;
  padding: 6px 8px;
}

.penalty-input {
  width: 80px;
}

.comment-input {
  min-width: 220px;
}

.create-form button:disabled,
.delete-button:disabled,
.edit-button:disabled,
.cancel-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.delete-button {
  padding: 5px;
  border: none;
  color: white;
  border-radius: 3px;
}

.edit-button {
  padding: 5px;
  border: none;
  color: white;
  background-color: #007bff;
  border-radius: 3px;
}

.cancel-button {
  padding: 5px 10px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: #fff;
  color: #333;
}

.hover:hover:not(:disabled) {
  cursor: pointer;
  background-color: rgba(224, 187, 72, 1);
}

.category-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
}

.category-top {
  background: #fff3cd;
  color: #856404;
}

.category-key {
  background: #cce5ff;
  color: #004085;
}

.category-regular {
  background: #e5e7eb;
  color: #666;
}

.actions-cell {
  white-space: nowrap;
}

.actions-cell .edit-button {
  margin-right: 4px;
}

.row-editing {
  background: rgba(224, 187, 72, 0.18);
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.list-content h2 {
  padding: 20px;
  text-align: center;
}

.company-table {
  margin-left: 20px;
  margin-right: 20px;
  border-collapse: collapse;
}

.company-table th,
.company-table td {
  text-align: center;
  padding: 10px;
  border: 1px solid #ccc;
}

.company-table th {
  font-weight: bold;
}

.comment-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state-block {
  padding: 24px;
  color: #667085;
  font-size: 14px;
  text-align: center;
}

.error-block {
  color: #c0392b;
}

.retry-btn {
  display: block;
  margin: 12px auto 0;
  padding: 6px 12px;
  border: 1px solid #c0392b;
  border-radius: 4px;
  background: #fff;
  color: #c0392b;
  cursor: pointer;
}
</style>
